// Copyright 2016 Sorama B.V.

/*
 * Adapted from: https://gist.run/?id=d9d8dd9df7be2dd2f59077bad3bfb399
 * as mentioned in: https://github.com/aurelia/binding/issues/443#issuecomment-232007548
 */
import { BindingEngine, bindingMode, customAttribute, DOM, inject } from "aurelia-framework";

@customAttribute("attribute-observer", bindingMode.twoWay)
@inject(DOM.Element, BindingEngine)
export class AttributeObserver {
    //#region Constants
    public static eventName = "attribute-changed";
    //#endregion

    //#region Injected Services
    private element: HTMLElement;
    private bindingEngine: BindingEngine;
    //#endregion

    //#region Fields

    // The attribute input value. Should be a whitespace-separated list of attribute names to observe.
    private value: string;

    private mutationObservers: { [attributeName: string]: MutationObserver };
    //#endregion

    constructor(element: HTMLInputElement, bindingEngine: BindingEngine) {
        this.element = element;
        this.bindingEngine = bindingEngine;
        this.mutationObservers = {};
    }

    private bind() {
        // Get attributes to observe
        const attributes = this.value
            .split(" ") // Split input on whitespace
            .filter((x) => !!x); // Remove empty elements (e.g. more then one space between attribute names in the value).

        // Create an observer for each attribute separately so we can properly handle old/new value checking
        attributes.forEach(this.createMutationObserverForAttribute);
    }

    private unbind() {
        for (const attribute in this.mutationObservers) {
            this.disconnectMutationObserverForAttribute(attribute);
        }
    }

    private createMutationObserverForAttribute = (attribute: string): void => {
        this.mutationObservers[attribute] = DOM.createMutationObserver((mutations: MutationRecord[]) => {
            this.elementAttributeChanged(attribute, mutations);
        });
        this.mutationObservers[attribute].observe(this.element, {
            attributes: true,
            attributeFilter: [attribute],
            attributeOldValue: true,
            childList: false,
            subtree: false,
            characterData: false,
        });
    };

    private disconnectMutationObserverForAttribute = (attribute: string): void => {
        this.mutationObservers[attribute].disconnect();
        this.mutationObservers[attribute] = undefined;
    };

    //#region Changed Callbacks
    private valueChanged = (newValue: string, oldValue: string): void => {
        const newAttributes = newValue.split(" ").filter((x) => !!x);
        const oldAttributes = oldValue.split(" ").filter((x) => !!x);

        const addedAttributes = newAttributes.filter((x) => !oldAttributes.some((y) => y === x));
        const removedAttributes = oldAttributes.filter((x) => !newAttributes.some((y) => y === x));

        addedAttributes.forEach(this.createMutationObserverForAttribute);
        removedAttributes.forEach((attribute) => {
            this.mutationObservers[attribute].disconnect();
        });
    };

    private elementAttributeChanged = (attribute: string, mutations: MutationRecord[]) => {
        const newValue = this.element.getAttribute(attribute);
        const oldValue = mutations[0].oldValue;

        const event = new CustomEvent(AttributeObserver.eventName, <CustomEventInit>{
            detail: {
                attribute: attribute,
                newValue: newValue,
                oldValue: oldValue,
            },
            bubbles: true,
        });

        this.element.dispatchEvent(event);
    };
    //#endregion
}
